import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { tasksApi, workflowsApi, extractList } from '../../api/facades';

const TasksListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [workflowId, setWorkflowId] = useState<number | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: workflowsRes } = useQuery({ queryKey: ['workflows'], queryFn: () => workflowsApi.list({ per_page: 100 }) });
  const workflows = extractList(workflowsRes);

  const { data: res, isLoading } = useQuery({
    queryKey: ['tasks', workflowId],
    queryFn: () => tasksApi.list({ workflow_id: workflowId === '' ? undefined : workflowId, per_page: 50 }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => tasksApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setDeleteId(null);
      toast.success('Task deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'workflow', label: 'Workflow', render: (r: any) => r.workflow?.name ?? '—' },
    { key: 'task_subject', label: 'Task' },
    { key: 'order', label: 'Order', render: (r: any) => r.order ?? '—' },
    { key: 'project_status', label: 'Status', render: (r: any) => r.project_status ?? '—' },
    { key: 'priority', label: 'Priority', render: (r: any) => r.priority ?? '—' },
    { key: 'depends_on', label: 'Depends on', render: (r: any) => (r.depends_on_task_id ? `Task #${r.depends_on_task_id}` : '—') },
    { key: 'assignees', label: 'Assignees', render: (r: any) => (r.members?.length ? r.members.map((m: any) => m.user?.people?.full_name ?? m.user?.email).filter(Boolean).join(', ') : '—') },
  ];

  return (
    <div>
      <PageHeader hideTitle title="Tasks" subtitle="Plan and track work (workflow tasks)." primaryAction={{ label: 'New Task', to: '/dashboard/tasks/new' }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
          <select
            value={workflowId === '' ? '' : workflowId}
            onChange={(e) => setWorkflowId(e.target.value === '' ? '' : Number(e.target.value))}
            className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
          >
            <option value="">All workflows</option>
            {workflows.map((w: any) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No tasks."
          onRowClick={(id) => navigate(`/dashboard/tasks/${id}`)}
          onEdit={(id) => navigate(`/dashboard/tasks/${id}/edit`)}
          onDelete={(id) => setDeleteId(Number(id))}
        />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete task" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default TasksListPage;
